import React, { useEffect } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Box, Card, CardActionArea, CardContent, Container, CssBaseline, Typography } from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { grey } from '@mui/material/colors';

import { useAuth } from '../../components/AuthProvider';
import OnuLogo from '../../components/imgs/onusLogo.png';
import HermesLogo from '../../components/imgs/hermesLogo.png';
import backgroundImg from '../../components/imgs/background.jpg';

const theme = createTheme();

function getServiceFromDomain() {
  const hostname = window.location.hostname.toLowerCase();

  if (hostname.startsWith('onus') || hostname.includes('onus.ipv7')) {
    return 'provisioning';
  }

  if (hostname.startsWith('hermes') || hostname.includes('hermes.ipv7')) {
    return 'hermes';
  }

  return null;
}

export default function Home() {
  const auth = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'IPV7 - Selecione um Serviço';
  }, []);

  if (!auth?.user) {
    return <Navigate to="/login" replace />;
  }

  const detectedService = getServiceFromDomain();
  if (detectedService) {
    return <Navigate to={`/${detectedService}`} replace />;
  }

  const services = [
    {
      id: 'provisioning',
      name: 'Provisioning',
      logo: OnuLogo,
      path: '/provisioning',
      description: 'Gerenciamento de ONUs',
    },
    {
      id: 'hermes',
      name: 'Hermes',
      logo: HermesLogo,
      path: '/hermes',
      description: 'Sistema de Backup',
    },
  ];

  const availableServices = services.filter((s) => auth.hasService(s.id));

  if (availableServices.length === 1) {
    return <Navigate to={availableServices[0].path} replace />;
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundImage: `url(${backgroundImg})`,
          backgroundPosition: 'center',
          backgroundSize: 'cover',
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="h4"
            component="h1"
            align="center"
            sx={{
              mb: 4,
              color: 'white',
              textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
            }}
          >
            Selecione um Serviço
          </Typography>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: 4,
              flexWrap: 'wrap',
            }}
          >
            {availableServices.map((service) => (
              <Card
                key={service.id}
                sx={{
                  width: 250,
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: 8,
                  },
                }}
              >
                <CardActionArea onClick={() => navigate(service.path)}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: 150,
                      backgroundColor: grey[100],
                      p: 2,
                    }}
                  >
                    <img
                      src={service.logo}
                      alt={service.name}
                      style={{
                        maxWidth: '80%',
                        maxHeight: '100%',
                        objectFit: 'contain',
                      }}
                    />
                  </Box>
                  <CardContent>
                    <Typography gutterBottom variant="h5" component="div" align="center">
                      {service.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" align="center">
                      {service.description}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            ))}

            {availableServices.length === 0 && (
              <Typography
                variant="h6"
                sx={{
                  color: 'white',
                  textShadow: '1px 1px 2px rgba(0,0,0,0.5)',
                }}
              >
                Nenhum serviço disponível para sua conta.
              </Typography>
            )}
          </Box>
        </Container>
      </Box>
    </ThemeProvider>
  );
}
